/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Central GraphQL Error Handler
 * ---------------------------------
 * WHY:
 * - Captures GraphQL errors
 * - Normalizes messages
 * - Ensures consistent error handling across project
 */

import { useAuthFlow } from '@/composables/useAuthFlow'
import { AuthView } from '@/types/auth'

/**
 * Magento GraphQL `generateCustomerToken` failure: wrong password, unknown email,
 * or temporarily locked — same `extensions.code` for all. Must not trigger the
 * forced “session expired” login shell (TK-10323).
 */
export const GQL_CUSTOMER_SIGN_IN_INCORRECT_OR_LOCKED = 'CUSTOMER_SIGN_IN_INCORRECT_OR_LOCKED'

export interface GqlErrorPayload {
    message?: string
    status?: number
    graphQLErrors?: Array<{
        message: string
        extensions?: {
            category?: string
            /** Magento / Adobe Commerce GraphQL error code when present */
            code?: string
            service?: string
        }
    }>
    response?: Response
}

/**
 * True when this GraphQL error is an auth-category error that should open the
 * forced re-login flow — excluding credential failures on sign-in.
 */
function shouldOpenForcedReauthLogin(
    gqlError: NonNullable<GqlErrorPayload['graphQLErrors']>[0]
): boolean {
    const category = gqlError?.extensions?.category
    const isGraphqlAuthError =
        category === 'graphql-authentication' || category === 'graphql-authorization'
    if (!isGraphqlAuthError) return false
    if (gqlError.extensions?.code === GQL_CUSTOMER_SIGN_IN_INCORRECT_OR_LOCKED) {
        return false
    }
    return true
}

/**
 * Handles GraphQL errors globally.
 *
 * @param operationName - GraphQL operation name from `wrapGqlRequest` (e.g. `generateCustomerToken`).
 *   Included on the thrown error for debugging; the sign-in guard uses `extensions.code`, not the name.
 */
export const gqlHandleError = async (
    error: GqlErrorPayload,
    params?: Record<string, any>,
    url?: string,
    operationName?: string
): Promise<never> => {
    const { open } = useAuthFlow()

    let status = error?.status || 500
    let description = 'Unexpected error while communicating with the cloud'

    // Case 1: GraphQL business errors
    if (error.graphQLErrors?.length) {
        const gqlError = error.graphQLErrors[0]
        if (gqlError && shouldOpenForcedReauthLogin(gqlError)) {
            open(
                AuthView.Login,
                'Your session has expired. Please log back in to continue.<br>You may return home, but you will lose your progress.',
                true
            )
        }

        status = 200
        description = error.graphQLErrors[0]?.message || 'GraphQL API Error'
    }
    // Case 2: HTTP response error
    else if (error.response) {
        status = error.response.status
        description = `Designer Error ${status}`
    }
    // Case 3: Runtime error
    else if (error.message) {
        description = error.message
    }

    // Throw normalized error to UI layer
    throw {
        message: description,
        status,
        params,
        url,
        ...(operationName ? { operationName } : {}),
    }
}
